import axios from 'axios';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPaidEndpointAppCheckToken } from '../firebaseHelper';
import TrackInfoRow from './TrackInfoRow';
import SearchResultsLoading from './SearchResultsLoading';
import SearchEmptyState from './SearchEmptyState';

const EpisodeSearchResults = ({
    query,
    podcastId,
    titleSpeakerId,
    entityName,
    includesVerticalCardPadding = true,
}) => {
    const navigate = useNavigate();
    const searchQuery = (query || '').trim();
    const [retry, setRetry] = useState(0);
    const [result, setResult] = useState(null);
    const requestKey = JSON.stringify([searchQuery, podcastId, titleSpeakerId, retry]);

    useEffect(() => {
        if (!searchQuery) return;

        const controller = new AbortController();
        let current = true;
        setResult({ requestKey, status: 'loading' });

        const search = async () => {
            try {
                const appCheckToken = await getPaidEndpointAppCheckToken();
                if (!current) return;
                const { data } = await axios.get('/episode_search', {
                    params: {
                        query: searchQuery,
                        ...(podcastId ? { podcastId } : {}),
                        ...(titleSpeakerId ? { titleSpeakerId } : {}),
                        limit: 20,
                    },
                    headers: { 'X-Firebase-AppCheck': appCheckToken },
                    signal: controller.signal,
                    timeout: 12000,
                });
                if (!current) return;
                if (!Array.isArray(data?.trackInfos) || data.trackInfos.some(track =>
                    !track || typeof track.id !== 'string' || !track.id.trim())) {
                    throw new Error('Invalid episode search response');
                }
                setResult({ requestKey, status: 'ready', trackInfos: data.trackInfos });
            } catch {
                if (current) setResult({ requestKey, status: 'error' });
            }
        };
        search();

        return () => {
            current = false;
            controller.abort();
        };
    }, [searchQuery, podcastId, titleSpeakerId, requestKey]);

    if (!searchQuery) return null;

    const padding = includesVerticalCardPadding ? '10px 0px' : '20px 0px 16px';
    if (result?.requestKey !== requestKey || result.status === 'loading') {
        return (
            <div style={{ padding }}>
                <SearchResultsLoading kind='episodes' includesVerticalCardPadding={includesVerticalCardPadding} />
            </div>
        );
    }

    if (result.status === 'error') {
        return (
            <div role='alert'>
                <SearchEmptyState message="Couldn't load episodes. Please try again." />
                <div style={{ textAlign: 'center', paddingBottom: '20px' }}>
                    <button type='button' className='clampSafeButton' onClick={() => setRetry(retry + 1)}>
                        Try again
                    </button>
                </div>
            </div>
        );
    }

    if (result.trackInfos.length === 0) {
        const scopeDescription = entityName ? ` for ${entityName}` : '';
        return <SearchEmptyState message={`No episodes matching "${searchQuery}"${scopeDescription}.`} />;
    }

    return (
        <div style={{ padding }}>
            <div className='trackInfoRowsVerticalList' aria-label='Episode results'>
                {result.trackInfos.map((trackInfo, index) => (
                    <TrackInfoRow
                        key={trackInfo.id}
                        trackInfo={trackInfo}
                        preferredSpeakerId={titleSpeakerId}
                        playTrack={(track, startSeconds = 0) => () => navigate(`/track/${encodeURIComponent(track.id)}?autoplay=true&start=${startSeconds}`)}
                        includesVerticalCardPadding={includesVerticalCardPadding}
                        showsBottomDivider={index !== result.trackInfos.length - 1}
                    />
                ))}
            </div>
        </div>
    );
};

export default EpisodeSearchResults;
